export const POSTHOG_EVENT = Object.freeze({
  SCREEN_VIEWED: 'screen_viewed',
  FEATURE_ACTION: 'feature_action',
  API_OPERATION_COMPLETED: 'api_operation_completed',
  CLIENT_ERROR: 'client_error',
})

const ALLOWED_CUSTOM_EVENTS = new Set(Object.values(POSTHOG_EVENT))
const ALLOWED_CUSTOM_PROPERTIES = new Set([
  'action',
  'app_version',
  'error_code',
  'error_type',
  'feature',
  'http_status',
  'outcome',
  'screen',
  'source',
])
const ALLOWED_FEATURES = new Set([
  'app',
  'attendance',
  'auth',
  'coaching',
  'daily_log',
  'home',
  'image_upload',
  'legal',
  'navigation',
  'planner',
  'preferences',
  'privacy',
  'profile',
  'reports',
  'routine_goals',
  'timer',
])
const ALLOWED_FEATURE_ACTIONS = new Set([
  'close_panel',
  'confirm_save_missing',
  'consent_check_failed',
  'consent_required',
  'discard',
  'hide_sidebar',
  'open_panel',
  'pause',
  'receive_response',
  'resume',
  'save',
  'send_message',
  'show_sidebar',
  'start',
  'stop',
  'take_ownership',
  'upload',
  'use_dark_mode',
  'use_light_mode',
])
const ALLOWED_API_RESOURCES = new Set([
  'account_withdrawal',
  'attendance_proof',
  'coaching',
  'daily_log',
  'daily_log_analytics',
  'email_verification',
  'image_upload',
  'legal_document',
  'login',
  'membership',
  'password',
  'plan',
  'plan_analytics',
  'profile',
  'report',
  'report_reference',
  'routine_goal',
])
const ALLOWED_OUTCOMES = new Set(['failure', 'incomplete', 'started', 'success'])
const ALLOWED_SCREENS = new Set([
  'consent_settings',
  'daily_log',
  'home',
  'plan_list',
  'profile',
  'report_create',
  'report_detail',
  'report_edit',
  'report_list',
  'required_consent',
  'routine_goals',
  'weekly_plan',
])
const ALLOWED_SOURCES = new Set(['error_boundary', 'promise', 'window'])
const ALLOWED_ERROR_TYPES = new Set([
  'api',
  'client',
  'network',
  'offline',
  'timeout',
])
const ALLOWED_SDK_PROPERTIES = new Set([
  '$browser',
  '$browser_language',
  '$browser_language_prefix',
  '$browser_version',
  '$device',
  '$device_id',
  '$device_type',
  '$insert_id',
  '$lib',
  '$lib_version',
  '$locale',
  '$os',
  '$os_version',
  '$process_person_profile',
  '$raw_user_agent',
  '$screen_height',
  '$screen_width',
  '$session_id',
  '$time',
  '$user_agent',
  '$viewport_height',
  '$viewport_width',
  '$window_id',
  'distinct_id',
  'token',
])
const SENSITIVE_DEFAULT_PROPERTIES = new Set([
  '$current_url',
  '$elements',
  '$host',
  '$initial_current_url',
  '$initial_host',
  '$initial_pathname',
  '$initial_referrer',
  '$initial_referring_domain',
  '$pathname',
  '$referrer',
  '$referring_domain',
  '$set',
  '$set_once',
])
const ALLOWED_WEB_VITAL_FIELDS = new Set([
  'delta',
  'id',
  'name',
  'navigationType',
  'rating',
  'value',
])
const SAFE_STRING_MAX_LENGTH = 80

function sanitizeScalar(value) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string') return undefined
  return value.trim().slice(0, SAFE_STRING_MAX_LENGTH)
}

function isAllowedAction(value) {
  if (ALLOWED_FEATURE_ACTIONS.has(value)) return true
  const match = /^(?:create|delete|read|update)_(.+)$/.exec(value)
  return Boolean(match && ALLOWED_API_RESOURCES.has(match[1]))
}

function sanitizeCustomProperty(key, value) {
  const sanitized = sanitizeScalar(value)
  if (sanitized === undefined) return undefined

  if (key === 'action') return isAllowedAction(sanitized) ? sanitized : undefined
  if (key === 'app_version') {
    return /^[0-9A-Za-z][0-9A-Za-z.+-]{0,39}$/.test(sanitized) ? sanitized : undefined
  }
  if (key === 'error_code') {
    return /^[A-Z][A-Z0-9_]{0,79}$/.test(sanitized) ? sanitized : undefined
  }
  if (key === 'error_type') return ALLOWED_ERROR_TYPES.has(sanitized) ? sanitized : undefined
  if (key === 'feature') return ALLOWED_FEATURES.has(sanitized) ? sanitized : undefined
  if (key === 'http_status') {
    return typeof sanitized === 'number' && sanitized >= 100 && sanitized <= 599
      ? sanitized
      : undefined
  }
  if (key === 'outcome') return ALLOWED_OUTCOMES.has(sanitized) ? sanitized : undefined
  if (key === 'screen') return ALLOWED_SCREENS.has(sanitized) ? sanitized : undefined
  if (key === 'source') return ALLOWED_SOURCES.has(sanitized) ? sanitized : undefined
  return undefined
}

function sanitizeSdkProperty(key, value) {
  if (key === '$process_person_profile') {
    return typeof value === 'boolean' ? value : undefined
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string') return undefined

  const maxLength = ['$raw_user_agent', '$user_agent'].includes(key) ? 512 : 160
  return value.slice(0, maxLength)
}

function sanitizeWebVitalDetail(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined

  return Object.entries(value).reduce((result, [key, detail]) => {
    if (!ALLOWED_WEB_VITAL_FIELDS.has(key)) return result
    const sanitized = sanitizeScalar(detail)
    if (sanitized !== undefined) result[key] = sanitized
    return result
  }, {})
}

function removeSensitiveDefaultProperties(properties) {
  const sanitized = { ...properties }

  SENSITIVE_DEFAULT_PROPERTIES.forEach(key => delete sanitized[key])
  Object.keys(sanitized).forEach(key => {
    if (key.startsWith('$utm_') || key.startsWith('$element_')) delete sanitized[key]
  })

  return sanitized
}

function sanitizeCustomEventProperties(properties) {
  return Object.entries(properties).reduce((result, [key, value]) => {
    if (ALLOWED_SDK_PROPERTIES.has(key)) {
      const sanitized = sanitizeSdkProperty(key, value)
      if (sanitized !== undefined) result[key] = sanitized
      return result
    }
    if (!ALLOWED_CUSTOM_PROPERTIES.has(key)) return result

    const sanitized = sanitizeCustomProperty(key, value)
    if (sanitized !== undefined) result[key] = sanitized
    return result
  }, {})
}

function sanitizeWebVitalsProperties(properties) {
  return Object.entries(properties).reduce((result, [key, value]) => {
    if (/^\$web_vitals_(CLS|FCP|LCP|INP)_value$/.test(key)) {
      const sanitized = sanitizeScalar(value)
      if (sanitized !== undefined) result[key] = sanitized
      return result
    }

    if (/^\$web_vitals_(CLS|FCP|LCP|INP)_event$/.test(key)) {
      const sanitized = sanitizeWebVitalDetail(value)
      if (sanitized) result[key] = sanitized
      return result
    }

    if (ALLOWED_SDK_PROPERTIES.has(key)) {
      const sanitized = sanitizeSdkProperty(key, value)
      if (sanitized !== undefined) result[key] = sanitized
    }
    return result
  }, {})
}

export function sanitizePosthogProperties(properties = {}) {
  return sanitizeCustomEventProperties(removeSensitiveDefaultProperties(properties))
}

export function sanitizePosthogEvent(event) {
  if (!event || typeof event !== 'object') return null

  const properties = removeSensitiveDefaultProperties(event.properties || {})
  if (event.event === '$web_vitals') {
    return {
      ...event,
      properties: sanitizeWebVitalsProperties(properties),
    }
  }
  if (!ALLOWED_CUSTOM_EVENTS.has(event.event)) return null

  return {
    ...event,
    properties: sanitizeCustomEventProperties(properties),
  }
}
